import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router';

import { Box } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import AutoLockSubsection from 'app/pages/loanApplication/AutoLockSubsection';
import ChannelSectionGate from 'app/pages/loanApplication/ChannelSectionGate';
import { LoanEditorSection } from 'app/pages/programs/channels/Channels.constants';
import {
  AutoLockFormsLevelType,
  PropertyAndLoanSubsectionNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';

import { LoanApplicationTabRouteParam } from 'constants/loanApplication';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import { getConditionalArray } from 'utils/data';

import {
  getChattelDetailsAction,
  getLoanAndLockSummaryEditDataAction,
  getPropertiesActionV2,
  getPropertyAndLoanSummaryAction,
} from 'store/loanApplications/actions';
import {
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectPropertyAndLoanSummarySubjectPropertyData,
} from 'store/loanApplications/selectors';

import ChattelDetailsSection from './ChattelDetailsSection';
import EditModal from './EditModal';
import { ENTITY_TYPE_HREF_MAP, PROPERTY_AND_LOAN_ENTITY_TYPES } from './EditModal/EditModal.hrefs';
import LoanAndLockInfo from './LoanAndLockInfo';
import { wrapperStyles } from './PropertyAndLoan.styles';
import PropertyAndLoanSummary from './PropertyAndLoanSummary';
import PropertyEnrichmentDrawer from './PropertyEnrichmentDrawer';
import SecondaryFinancing from './SecondaryFinancing';

const PropertyAndLoan = ({ isLoading, setIsLoading }) => {
  const { applicationId, subtab, entityId } = useParams();

  const dispatch = useDispatch();

  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);

  const isRealEstate = !isUnsecured && !isChattel;

  const subjectPropertyData = useSelector(selectPropertyAndLoanSummarySubjectPropertyData);

  const [isError, setIsError] = useState(false);
  const [isEnrichmentDrawerOpen, setIsEnrichmentDrawerOpen] = useState(false);

  const handleOpenEnrichmentDrawer = useCallback(() => {
    setIsEnrichmentDrawerOpen(true);
  }, []);

  const handleCloseEnrichmentDrawer = useCallback(() => {
    setIsEnrichmentDrawerOpen(false);
  }, []);

  const fetchData = useCallback(async () => {
    setIsError(false);
    setIsLoading(true);

    Promise.all([
      dispatch(getPropertyAndLoanSummaryAction({ applicationId })),
      dispatch(getLoanAndLockSummaryEditDataAction({ applicationId })),
      ...getConditionalArray(isRealEstate, () => [
        dispatch(getPropertiesActionV2({ applicationId, withSubjectProperty: true })),
      ]),
      ...getConditionalArray(isChattel, () => [
        dispatch(getChattelDetailsAction({ applicationId })),
      ]),
    ])
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [applicationId, dispatch, setIsLoading, isRealEstate, isChattel]);

  usePendingChangesDependentFetch(fetchData);

  // LEN2-798: the loan's contacts moved to the Contacts tab; keep old contacts-drawer links working.
  if (subtab === ENTITY_TYPE_HREF_MAP.CONTACTS) {
    return (
      <Redirect
        to={LoansPaths.applicationManagement.generatePath({
          applicationId,
          tab: LoanApplicationTabRouteParam.CONTACTS,
          subtab,
          entityId,
        })}
      />
    );
  }

  return (
    <>
      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <Box sx={wrapperStyles}>
          <AutoLockSubsection
            areaName={PropertyAndLoanSubsectionNames.PROPERTY_INFO}
            levelType={AutoLockFormsLevelType.SUBSECTION}
          >
            {isRealEstate && (
              <PropertyAndLoanSummary
                isLoading={isLoading || isError}
                onOpenEnrichmentDrawer={handleOpenEnrichmentDrawer}
              />
            )}
            {isChattel && <ChattelDetailsSection isLoading={isLoading || isError} />}
          </AutoLockSubsection>
          <ChannelSectionGate section={LoanEditorSection.PRICING}>
            <AutoLockSubsection
              areaName={PropertyAndLoanSubsectionNames.LOAN_AND_LOCK_INFO}
              levelType={AutoLockFormsLevelType.SUBSECTION}
            >
              <LoanAndLockInfo isLoading={isLoading || isError} />
              {isRealEstate && <SecondaryFinancing isLoading={isLoading || isError} />}
            </AutoLockSubsection>
          </ChannelSectionGate>
        </Box>
      </ErrorHandlingLayout>

      <EditModal entityTypes={PROPERTY_AND_LOAN_ENTITY_TYPES} isLoading={isLoading} />

      <PropertyEnrichmentDrawer
        address={subjectPropertyData?.propertyAddress}
        isOpen={isEnrichmentDrawerOpen}
        propertyId={subjectPropertyData?.propertyId}
        reapiPropertyId={subjectPropertyData?.reapiPropertyId}
        onClose={handleCloseEnrichmentDrawer}
      />
    </>
  );
};

PropertyAndLoan.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(memo(PropertyAndLoan));
